import type { ChatPatchPayload, HomeChatMessages } from './chatTranscript';

export type WebSocketLike = Pick<WebSocket, 'readyState' | 'send' | 'close' | 'onopen' | 'onclose' | 'onmessage'>;

export type SandboxState = 'connecting' | 'allocating' | 'ready' | 'error';

export type HomeChatNotification =
  | { method: 'copilot/chat/didUpdate'; params: { conversationId: string; payload: ChatPatchPayload } }
  | { method: 'copilot/chat/setName'; params: { conversationId: string; name: string } }
  // With a `conversationId` it is that chat's pod; without, the broker connection itself.
  | { method: 'sandbox/status'; params: { state: SandboxState; message?: string; conversationId?: string } }
  | { method: 'copilot/agent/error'; params: { conversationId?: string; error: string; error_type: string } }
  | { method: 'session/auth'; params: { authorization: string } }
  // A turn started or ended in a chat this tab has open, by another tab.
  | { method: 'copilot/chat/turn'; params: { conversationId: string; running: boolean } }
  // What the chat's pod is in the middle of, sent when this tab opens it.
  | {
      method: 'copilot/chat/snapshot';
      params: { conversationId: string; running: boolean; elements: HomeChatMessages };
    };

export type HomeChatRequest = {
  chatId: string;
  messageId: string;
  message: string;
  context?: unknown;
};

export type HomeChatTurnResult = { content: string; success: boolean };

export class HomeChatBrokerError extends Error {
  constructor(
    message: string,
    readonly type?: string
  ) {
    super(message);
    this.name = 'HomeChatBrokerError';
  }
}

type Pending = { resolve: (result: unknown) => void; reject: (error: Error) => void };

type HomeChatSocketOptions = {
  getUrl: () => Promise<string>;
  onNotification: (notification: HomeChatNotification) => void;
  onConnectionChange?: (connected: boolean) => void;
  createSocket?: (url: string) => WebSocketLike;
  reconnectDelaysMs?: number[];
};

const WS_OPEN = 1;
const DEFAULT_RECONNECT_DELAYS_MS = [500, 1000, 2000, 5000, 10000];

/** One JSON-RPC-ish connection to the home chat broker, reconnecting with backoff until closed. */
export class HomeChatSocket {
  private socket: WebSocketLike | undefined;
  private readonly pending = new Map<string, Pending>();
  private outbox: string[] = [];
  private sequence = 0;
  private attempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private closedByClient = false;

  constructor(private readonly options: HomeChatSocketOptions) {}

  connect() {
    this.closedByClient = false;
    void this.open();
  }

  close() {
    this.closedByClient = true;
    clearTimeout(this.reconnectTimer);
    this.socket?.close();
    this.socket = undefined;
    this.rejectPending(new HomeChatBrokerError('Connection to DataGPT closed'));
  }

  chat(request: HomeChatRequest) {
    return this.request<HomeChatTurnResult>('copilot/agent/chat', { request });
  }

  /** Which chat is on screen, so the broker sends this tab that chat's turns from other tabs too. */
  openChat(conversationId: string | undefined) {
    return this.request<{ ok: boolean }>('copilot/chat/open', { conversationId });
  }

  stop(conversationId: string) {
    return this.request<{ ok: boolean }>('copilot/agent/stop', { conversationId });
  }

  respondToUserInput(params: {
    conversationId?: string;
    id: string;
    answers: Record<string, string>;
    cancelled: boolean;
  }) {
    return this.request<{ success: boolean }>('copilot/agent/userInputResponse', params);
  }

  readResource(uri: string) {
    return this.request<{ html: string }>('mcp/readResource', { uri });
  }

  callTool(name: string, args: Record<string, unknown>) {
    return this.request<unknown>('mcp/callTool', { name, arguments: args });
  }

  private request<T>(method: string, params: unknown): Promise<T> {
    const id = `home-chat-${++this.sequence}`;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (result: unknown) => void, reject });
      this.send(JSON.stringify({ id, method, params }));
    });
  }

  private send(frame: string) {
    if (this.socket?.readyState === WS_OPEN) this.socket.send(frame);
    else this.outbox.push(frame);
  }

  private async open() {
    let socket: WebSocketLike;
    try {
      const url = await this.options.getUrl();
      if (this.closedByClient) return;
      const createSocket = this.options.createSocket ?? ((socketUrl: string) => new WebSocket(socketUrl));
      socket = createSocket(url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.options.onConnectionChange?.(true);
      const queued = this.outbox;
      this.outbox = [];
      queued.forEach((frame) => socket.send(frame));
    };
    socket.onmessage = (event) => this.handleFrame(event.data);
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = undefined;
      this.options.onConnectionChange?.(false);
      this.rejectPending(new HomeChatBrokerError('Connection to DataGPT closed'));
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.closedByClient) return;
    const delays = this.options.reconnectDelaysMs ?? DEFAULT_RECONNECT_DELAYS_MS;
    const delay = delays[Math.min(this.attempt, delays.length - 1)];
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => void this.open(), delay);
  }

  private rejectPending(error: Error) {
    const pending = [...this.pending.values()];
    this.pending.clear();
    this.outbox = [];
    pending.forEach(({ reject }) => reject(error));
  }

  private handleFrame(data: unknown) {
    if (typeof data !== 'string') return;
    let frame: { id?: unknown; result?: unknown; error?: { message?: string; type?: string }; method?: unknown };
    try {
      frame = JSON.parse(data);
    } catch {
      return;
    }
    if (typeof frame.id === 'string') {
      const pending = this.pending.get(frame.id);
      if (!pending) return;
      this.pending.delete(frame.id);
      if (frame.error) {
        pending.reject(new HomeChatBrokerError(frame.error.message ?? 'DataGPT request failed', frame.error.type));
      } else {
        pending.resolve(frame.result);
      }
      return;
    }
    // The broker heartbeats with a JSON {method:'ping'} text frame (a WebSocket control ping would be
    // answered by the app proxy, not us, so the broker could never see this tab go away). Answer it so
    // the broker keeps the session — and its sandbox — alive; a missed pong tells it the tab is gone.
    if (frame.method === 'ping') {
      this.send(JSON.stringify({ method: 'pong' }));
      return;
    }
    if (typeof frame.method === 'string') this.options.onNotification(frame as HomeChatNotification);
  }
}
